<template>
  <v-form ref="form">
    <v-row class="align-baseline">
      <v-col cols="auto" align-self="center">
        <data-input :hint="$i18n.t('dialog.body.chooseLineColor')" v-model="color" :data-type="dataTypeColor">
        </data-input>
      </v-col>
      <v-col cols="3">
        <data-input
          :data-type="dataTypeSelect"
          :hint="$i18n.t('dialog.body.chooseModel')"
          persistent-hint
          :disabled="isFirst"
          v-model="model"
          class="select--custom"
          :items="modelsArray"
          attach
          item-text="name"
          item-value="id"
          :menu-props="{ offsetY: true }"
        />
      </v-col>
      <v-col cols="3">
        <data-input
          :data-type="dataTypeSelect"
          :hint="$i18n.t('dialog.body.chooseScenario')"
          persistent-hint
          :rules="[notFoundRule]"
          :disabled="isFirst || !model"
          v-model="scenario"
          class="select--custom"
          :items="selectedModelScenarios"
          attach
          item-text="name"
          item-value="id"
          :menu-props="{ offsetY: true }"
        >
        </data-input>
      </v-col>
      <v-col cols="3">
        <data-input
          :data-type="dataTypeSelect"
          :hint="$i18n.t('dialog.body.chooseParameter')"
          persistent-hint
          v-model="parameter"
          class="select--custom select--padding"
          @change="saveToLocalStorage"
          attach
          :items="computedSelectItems"
          :menu-props="{ offsetY: true }"
          height="16"
        >
          <template #item="{ item }">
            <v-tooltip bottom color="font" :disabled="!item.isDisabled">
              <template #activator="{ on: onTooltip }">
                <v-row
                  no-gutters
                  v-on="onTooltip"
                  @click="onClick($event, item)"
                  class="item align-center pa-4 text-left"
                  :class="{ 'text--disabled': item.isDisabled, 'item--disabled': item.isDisabled }"
                >
                  <span>
                    {{ item.text }}
                  </span>
                </v-row>
              </template>
              {{ $i18n.t('dialog.body.maxTwoParameters') }}
            </v-tooltip>
          </template>
        </data-input>
      </v-col>
      <v-col cols="auto" class="pr-0" v-if="!isFirst">
        <base-rounded-button
          :tooltip="$i18n.t('dialog.title.deleteScenario')"
          icon="$close"
          size="small"
          color="white"
          iconColor="italic"
          @click="$emit('removeScenarioPicker')"
          :has-shadow-effect="false"
        >
        </base-rounded-button>
      </v-col>
      <v-col cols="auto" v-if="isLast" class="pl-0">
        <base-rounded-button
          icon="mdi-plus"
          size="x-small"
          color="success"
          @click="$emit('addScenarioPicker')"
          :tooltip="$i18n.t('dialog.body.addScenario')"
        >
        </base-rounded-button>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { get } from 'vuex-pathify';
import { debounce } from 'lodash';

import DataInput from '@/components/DataInput';

export default {
  name: 'ModelingChartDialogScenarioPicker',
  components: {
    DataInput,
  },
  props: {
    id: {
      type: Number,
      requried: true,
    },
    value: {
      type: Object,
      required: true,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      required: true,
    },
    isFirst: {
      type: Boolean,
      default: false,
    },
    selectItems: {
      type: Array,
      required: true,
    },
    uniqueSelectedParameters: {
      type: Array,
      required: true,
    },
    selectedParameters: {
      type: Array,
      required: true,
    },
    netType: {
      type: String,
      required: true,
    },
    elementLabel: {
      type: [Number, String],
      required: true,
    },
  },
  data: () => ({
    dataTypeColor: {
      name: 'color',
    },
    dataTypeSelect: {
      name: 'select',
    },
  }),
  computed: {
    models: get('modeling/models@:netType'),
    modelsArray() {
      return this.$_objectToArray(this.models || []);
    },
    computedSelectItems() {
      return this.selectItems.map(item => {
        return {
          ...item,
          isDisabled: this.getIsDisabled(item),
          text: `${this.$i18n.t(`sidebar.${item.value}`)} ${item.unit ? `[${item.unit}]` : ''}`,
        };
      });
    },
    notFoundRule() {
      return () =>
        !this.scenarioNotFoundedElements.includes(this.elementLabel) ||
        this.$i18n.t('rules.notFoundModelingElement', {
          elementType: this.$i18n.t(`sidebar.${this.type}Genetive`),
          elementName: this.elementLabel,
        });
    },
    selectedModel() {
      return this.models[this.model];
    },
    selectedModelScenarios() {
      return this.selectedModel?.scenarios || [];
    },
    model: {
      get() {
        return +this.value.modelId;
      },
      set(nV) {
        this.debouncedInputNewValue('modelId', nV);
      },
    },
    scenario: {
      get() {
        return +this.value.scenarioId;
      },
      set(nV) {
        this.debouncedInputNewValue('scenarioId', nV);
      },
    },
    parameter: {
      get() {
        return this.value.parameter;
      },
      set(nV) {
        this.debouncedInputNewValue('parameter', nV);
      },
    },
    color: {
      get() {
        return this.value.color;
      },
      set(nV) {
        this.debouncedEmitNewValue('color', nV);
      },
    },
    notFoundedElements: get('modeling/scenariosNotFoundedElementsByName@:netType'),
    scenarioNotFoundedElements() {
      return this.notFoundedElements?.[this.scenario]?.[this.type] || [];
    },
  },
  methods: {
    saveToLocalStorage(nV) {
      if (this.isFirst) {
        localStorage.setItem(`modelingParameter${this.type}`, JSON.stringify(nV));
      }
    },
    emitNewValue(key, value) {
      this.$emit('input', { id: this.id, value: { ...this.value, [key]: value } });
    },
    getIsDisabled(item) {
      return (
        this.uniqueSelectedParameters.length === 2 &&
        !this.uniqueSelectedParameters.includes(item.value) &&
        (this.selectedParameters.filter(parameter => parameter === this.parameter).length > 1 || !this.parameter)
      );
    },
    onClick(e, item) {
      if (item.isDisabled) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
  },
  watch: {
    scenarioNotFoundedElements() {
      this.$refs.form?.validate();
    },
    selectedModelScenarios(nV, oV) {
      if (!this.isFirst && JSON.stringify(oV) !== JSON.stringify(nV)) {
        this.debouncedInputNewValue('scenarioId', nV[0]?.id || null);
      }
    },
  },
  created() {
    this.debouncedEmitNewValue = debounce(this.emitNewValue, 500);
    this.debouncedInputNewValue = debounce(this.emitNewValue, 50);
  },
  mounted() {
    if (this.isFirst) {
      this.parameter = localStorage.getItem(`modelingParameter${this.type}`)
        ? JSON.parse(localStorage.getItem(`modelingParameter${this.type}`))
        : this.computedSelectItems[0].value;
    }
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .select--padding .v-list-item {
    padding: 0;
  }
}
.item {
  min-height: inherit;
  width: 100%;
}
.item--disabled {
  cursor: not-allowed;
  // pointer-events: none;
}
</style>
